import { Outlet, useLocation, useNavigate } from 'react-router'
import { OnboardingLayout } from './OnboardingLayout'

/**
 * Layout route for the data-import flow (/onboarding/import + /onboarding/import/:id).
 *
 * The import pages belong to the onboarding family but were the only ones rendered
 * with no chrome at all — bare, edge-to-edge content with no top bar. This wraps
 * them in OnboardingLayout (FlowRent wordmark + Exit), exactly like their siblings
 * OnboardingWizardPage / ExplorerModePage, and centers the content in the standard
 * onboarding container.
 *
 * It is applied as a *layout route* (not self-wrapped inside each page) so the frame
 * stays mounted across the upload → processing/review step navigation, avoiding a
 * remount flicker — the same reason protected pages nest under ProtectedLayout.
 *
 * `showStepBar` stays off. EP-115 C-5 proposed flipping it on, but this is a layout
 * ROUTE — it has no access to the current step, which each page derives from its own
 * state (the upload page is always 1; the detail page computes it from the import's
 * status). Threading that up through context to satisfy a prop the pages can render
 * directly would be the wrong trade. Both pages render the labelled bar themselves at
 * `md+`, and OnboardingBottomBar renders the minimal one below `md` — the same split
 * the wizard uses.
 */
export function OnboardingImportLayout() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  // Two entry points share this layout. From the onboarding one, Exit means
  // "leave onboarding" and OnboardingLayout's own default (a link to `/`) is
  // right. From the in-app one, the user came from Properties and expects to be
  // put back there, not on the dashboard — the flow's Back/cancel already
  // returns there, and Exit disagreeing with it would be a small trap.
  const isInAppEntry = pathname.startsWith('/properties/import')

  return (
    <OnboardingLayout
      showStepBar={false}
      onSkip={isInAppEntry ? () => navigate('/properties') : undefined}
    >
      {/* Background flares (EP-115 U-9 / §6.3). Fixed to the viewport rather than
          the content box so a short step does not clip them to a hard rectangular
          edge, with `z-0` here and `relative z-10` on the content so panels sit
          over them. Values match the wizard's at OnboardingWizardPage.tsx:2509 —
          the Figma draws two large soft ellipses on every import frame too, at
          per-frame coordinates; a fixed pair reads the same and avoids
          re-positioning decoration per route. */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
        <div className="absolute left-[28%] top-[38%] h-[24rem] w-[24rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-green-300/25 blur-[120px]" />
        <div className="absolute left-[68%] top-[58%] h-[24rem] w-[24rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky-300/25 blur-[120px]" />
      </div>

      {/* No width constraint or padding here any more. The pages need to render
          the full-bleed step bar (ImportStepBar) as their first child, in the
          same band the wizard's occupies directly under the header; a container
          at this level made that impossible. Each page constrains its own
          CONTENT column instead. */}
      <Outlet />
    </OnboardingLayout>
  )
}
